export const SCHEDULING_BOOKING_NODE_TYPE = "schedule_booking";
export const SCHEDULING_BOOKING_OUTPUTS = [
  { id: "held", label: "Held" },
  { id: "send_failed", label: "Send failed" },
  { id: "timed_out", label: "Timed out" },
  { id: "processing_failed", label: "Processing failed" },
];

export const createSchedulingBookingConfig = () => ({
  schemaVersion: 1,
  dataChannelMode: "DataExchange",
  flowDefinitionId: "",
  flowRevisionId: "",
  offeringId: "",
  entry: { bodyText: "", buttonText: "Book time", startScreenId: "" },
  timeoutMinutes: 30,
  serviceArea: null,
  preferredResource: null,
  durationSessionKey: null,
  requestDetails: null,
  endpointless: null,
  bindings: {
    bookingScreenId: "",
    dateComponent: "booking_date",
    dateDataKey: "booking_date",
    slotComponent: "booking_slot",
    slotDataKey: "available_slots",
    slotEnabledDataKey: "slots_enabled",
    messageDataKey: "scheduling_message",
    actionDataKey: "scheduling_action",
    loadSlotsAction: "load_slots",
    confirmHoldAction: "confirm_hold",
    terminalSuccessScreen: "",
  },
});

const identifier = value => /^[A-Za-z0-9_.-]{1,200}$/.test(String(value || ""));
const sessionKey = value => /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/.test(String(value || ""));
const bindingValid = binding => !binding
  || (binding.kind === "Fixed" && Boolean(binding.fixedId) && !binding.sessionKey)
  || (binding.kind === "SessionValue" && !binding.fixedId && sessionKey(binding.sessionKey));

export function validateSchedulingBookingConfig(config) {
  if (config?.schemaVersion !== 1 || !["DataExchange", "Endpointless"].includes(config?.dataChannelMode))
    return { isValid: false, message: "Schedule Booking configuration version is invalid." };
  if (!config?.flowDefinitionId || !config?.flowRevisionId || !config?.offeringId)
    return { isValid: false, message: "Select a Data Exchange revision and Scheduling Offering." };
  if (!config?.entry?.buttonText?.trim() || config.entry.buttonText.trim().length > 20 || !identifier(config?.entry?.startScreenId))
    return { isValid: false, message: "Configure a valid entry button and start screen." };
  if (Number(config?.timeoutMinutes) < 5 || Number(config?.timeoutMinutes) > 10080)
    return { isValid: false, message: "Timeout must be between 5 and 10080 minutes." };
  if (!bindingValid(config?.serviceArea) || !bindingValid(config?.preferredResource)
    || (config?.durationSessionKey && !sessionKey(config.durationSessionKey)))
    return { isValid: false, message: "A fixed/session input binding is invalid." };
  if (config.dataChannelMode === "Endpointless") {
    const date = config.endpointless?.dateResponseKey;
    const time = config.endpointless?.timeResponseKey;
    if (!identifier(date) || !identifier(time) || date === time || config.requestDetails?.fields?.some(f => [date, time].includes(f.responseKey)))
      return { isValid: false, message: "Configure distinct date/time answer keys outside booking details." };
    const error = validateBookingDetails(config.requestDetails);
    return { isValid: !error, message: error };
  }
  const b = config?.bindings || {};
  const required = ["bookingScreenId", "dateComponent", "dateDataKey", "slotComponent", "slotDataKey",
    "slotEnabledDataKey", "messageDataKey", "actionDataKey", "loadSlotsAction", "confirmHoldAction", "terminalSuccessScreen"];
  if (required.some(key => !identifier(b[key])) || b.loadSlotsAction === b.confirmHoldAction)
    return { isValid: false, message: "Scheduling Flow bindings are incomplete or conflicting." };
  if (new Set([b.slotDataKey, b.slotEnabledDataKey, b.messageDataKey, b.actionDataKey]).size !== 4)
    return { isValid: false, message: "Scheduling response/action data keys must be unique." };
  const detailsError = validateBookingDetails(config.requestDetails, b);
  if (detailsError) return { isValid: false, message: detailsError };
  return { isValid: true, message: "" };
}

export function validateBookingDetails(details, bindings = {}) {
  if (!details) return "";
  const keyValid = value => /^[a-z0-9_.-]{1,128}$/.test(String(value || ""));
  const guid = value => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || "")) && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(value);
  const fields = details.fields;
  if (!keyValid(details.dataKey) || [bindings.actionDataKey, bindings.dateDataKey, bindings.slotDataKey, "error"].includes(details.dataKey)
    || !Array.isArray(fields) || fields.length < 1 || fields.length > 20 || fields.some(f => !f)
    || new Set(fields.map(f => f.responseKey)).size !== fields.length || new Set(fields.map(f => f.fieldId)).size !== fields.length)
    return "Configure unique booking answer keys and destination fields (maximum 20).";
  for (const field of fields) {
    if (!keyValid(field.responseKey) || !guid(field.fieldId) || !["Text", "Phone", "Integer"].includes(field.responseType)
      || (field.maxLength != null && (!Number.isInteger(field.maxLength) || field.maxLength < 1 || field.maxLength > 4000))
      || [field.minimum, field.maximum].some(v => v != null && (!Number.isSafeInteger(v) || field.responseType !== "Integer"))
      || (field.minimum != null && field.maximum != null && field.minimum > field.maximum)
      || (field.allowedValues != null && (field.responseType !== "Text" || !Array.isArray(field.allowedValues)
        || field.allowedValues.length < 1 || field.allowedValues.length > 100
        || field.allowedValues.some(v => typeof v !== "string" || !v.trim() || v.length > 200)
        || new Set(field.allowedValues).size !== field.allowedValues.length)))
      return "A booking details field has an invalid type, destination or validation limit.";
    if (field.requiredWhen) {
      const selector = fields.find(f => f.responseKey === field.requiredWhen.responseKey);
      if (field.required || field.responseType !== "Text" || field.allowedValues != null || selector === field || !selector?.required
        || selector.requiredWhen || selector.responseType !== "Text" || !selector.allowedValues?.includes(field.requiredWhen.equalsValue))
        return "A conditional booking field must refer to one allowed value of a required choice answer.";
    }
  }
  return "";
}
